"use client";

import { GetInboxResponseDTO, InboxChat } from "@/app/api/chats/inbox/inbox.dto";
import ChatBubble from "@/components/common/chat-bubble";
import useIsMobile from "@/hooks/useIsMobile";
import { CHAT_INBOX_API, CHATS_API_BASE_URL } from "@/lib/constants";
import { cn } from "@/lib/utils";
import ChatWidget from "@/modules/chat-area/components/chat-widget";
import OpenChatTrigger from "@/modules/chat-area/components/open-chat-trigger";
import { createDirectChat, getChatUsingUniqueChatID } from "@/services/chat/chat";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useParams, usePathname, useRouter } from "next/navigation";
import {
	createContext,
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import toast from "react-hot-toast";

interface ChatContext {
	activeChat?: InboxChat;
	inbox: {
		chats?: InboxChat[];
		isError: boolean;
		isLoading: boolean;
	};
	showChatWidget: boolean;
	showListOfChatsInInbox: boolean;
	activeTab: "group" | "direct";
	groupChats: InboxChat[];
	directChats: InboxChat[];
	pinnedChatUniqueIds: string[];
	handleOpenChatWidget: () => void;
	handleCloseChatWidget: () => void;
	handleOpenChat: ({
		receiverUserId,
		uniqueChatId,
	}: {
		receiverUserId?: string;
		uniqueChatId?: string;
	}) => void;
	handleCloseChat: () => void;
	startConversation: ({
		receiverUserId,
		uniqueChatId,
	}: {
		receiverUserId?: string;
		uniqueChatId?: string;
	}) => Promise<void>;
	handleOpenInbox: () => void;
	handleCloseInbox: () => void;
	handleTabChange: (tab: "group" | "direct") => void;
	handleChatChange: (uniqueChatId: string) => void;
	handlePinChat: (uniqueChatId: string) => void;
	isPinnedChat: (uniqueChatId: string) => boolean;
}

const ChatContext = createContext<ChatContext | undefined>(undefined);

const fetchInbox = async () => {
	try {
		const response = await fetch(CHAT_INBOX_API, { next: { tags: ["inbox"] } });
		if (!response.ok) {
			return {
				inbox: [],
			};
		}

		const data = await response.json();

		return data;
	} catch (error) {
		console.log("Error occured while fetching inbox");

		return {
			inbox: [],
		};
	}
};

const ChatProvider = ({ children }: { children: React.ReactNode }) => {
	const {
		isLoading: isInboxLoading,
		isError: isInboxError,
		data,
	} = useQuery<GetInboxResponseDTO>({
		queryKey: ["inbox"],
		queryFn: fetchInbox,
		refetchOnWindowFocus: false,
	});

	const inbox = data?.data?.inbox;
	const pinnedChats = useMemo(() => data?.data?.pinnedChats || [], [data]);

	const params = useParams() as {
		"community-slug"?: string;
		"channel-slug"?: string;
	};

	const channelSlug = params["channel-slug"];

	const router = useRouter();
	const pathname = usePathname();
	const queryClient = useQueryClient();
	const isMobileDevice = useIsMobile();

	const [groupChats, setGroupChats] = useState<InboxChat[]>([]);
	const [showListOfChatsInInbox, setShowListOfChatInInbox] = useState<boolean>(false);
	const [directChats, setDirectChats] = useState<InboxChat[]>([]);
	const [showChatWidget, setShowChatWidget] = useState<boolean>(true);
	const [activeTab, setActiveTab] = useState<"group" | "direct">("group");
	const [activeChat, setActiveChat] = useState<InboxChat | undefined>(undefined);
	const [filteredInbox, setFilteredInbox] = useState<InboxChat[] | undefined>(inbox);
	const [pinnedChatUniqueIds, setPinnedChatUniqueIds] = useState<string[]>([]);

	const pagesToHideChatWidget = [
		"/chat",
		"/forums",
		"/communities",
		"/products/form",
		"/services/oneNDF",
		"/news",
	];
	const shouldHideChatWidget = pagesToHideChatWidget.some((page) => pathname.startsWith(page));

	const startConversation = useCallback(
		async ({
			receiverUserId,
			uniqueChatId,
		}: {
			receiverUserId?: string;
			uniqueChatId?: string;
		}) => {
			if (receiverUserId) {
				// For direct messages
				const chatFromInbox = filteredInbox?.find((chat) => chat?.recipient?.id === receiverUserId);

				if (chatFromInbox) {
					// Existing chat
					setShowListOfChatInInbox(false);
					setShowChatWidget(true);
					setActiveChat(chatFromInbox);
				} else {
					// New chat
					const chatRecord = await createDirectChat({ receiverUserId: receiverUserId });
					const newChat = chatRecord.data;

					if (chatRecord.success && newChat) {
						const updatedFilteredInbox = filteredInbox ? [newChat, ...filteredInbox] : [newChat];

						queryClient.setQueryData(["inbox"], updatedFilteredInbox);

						setShowChatWidget(true);
						setFilteredInbox(updatedFilteredInbox);
						setActiveChat(newChat);
					} else {
						toast.error(chatRecord.message || "Failed to start a conversation");
					}
				}
			}

			if (uniqueChatId) {
				// For group messages
				const chatFromInbox = filteredInbox?.find((chat) => chat?.uniqueChatId === uniqueChatId);

				if (chatFromInbox) {
					// Existing chat
					setShowListOfChatInInbox(false);
					setActiveChat(chatFromInbox);

					router.push(`/chat?chatId=${chatFromInbox.uniqueChatId}`);
				} else {
					// New chat
					const chatRecord = await getChatUsingUniqueChatID({ uniqueChatId });
					const newChat = chatRecord.data;

					if (chatRecord.success && newChat) {
						const updatedFilteredInbox = filteredInbox ? [newChat, ...filteredInbox] : [newChat];

						queryClient.setQueryData(["inbox"], updatedFilteredInbox);
						setFilteredInbox(updatedFilteredInbox);
						setActiveChat(newChat);

						router.push(`/chat?chatId=${newChat.uniqueChatId}`);
					} else {
						toast.error(chatRecord.message || "Failed to get conversation info");
					}
				}
			}
		},
		[filteredInbox, queryClient, router]
	);

	// Pin chat
	const pinChat = useCallback(async (chatId: string) => {
		try {
			const url = new URL(`${CHATS_API_BASE_URL}/${chatId}/pin`);
			const response = await fetch(url, {
				method: "POST",
			});

			const { success, message } = await response.json();

			if (success) {
				toast.success(message);
			} else {
				toast.error(message);
			}
		} catch (error) {
			console.log("Error occurred while pinning chat", error);
		}
	}, []);

	const pinChatMutation = useMutation({
		mutationFn: ({ chatId }: { chatId: string }) => pinChat(chatId),
		onMutate: async (newChat) => {
			if (newChat.chatId) {
				setPinnedChatUniqueIds((prevPinnedChatUniqueIds) => [
					...prevPinnedChatUniqueIds,
					newChat.chatId,
				]);
			}
		},
		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: ["inbox"] });
		},
	});

	// Unpin chat
	const unpinChat = useCallback(async (chatId: string) => {
		try {
			const url = new URL(`${CHATS_API_BASE_URL}/${chatId}/unpin`);
			const response = await fetch(url, {
				method: "POST",
			});

			const { success, message } = await response.json();

			if (success) {
				toast.success(message);
			} else {
				toast.error(message);
			}
		} catch (error) {
			console.log("Error occurred while unpinning chat", error);
		}
	}, []);

	const unPinChatMutation = useMutation({
		mutationFn: ({ chatId }: { chatId: string }) => unpinChat(chatId),
		onMutate: async (newChat) => {
			if (newChat.chatId) {
				setPinnedChatUniqueIds((prevPinnedChatUniqueIds) =>
					prevPinnedChatUniqueIds.filter((pinnedChat) => pinnedChat !== newChat.chatId)
				);
			}
		},
		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: ["inbox"] });
		},
	});

	const isPinnedChat = useCallback(
		(uniqueChatId: string) => {
			const isPinned = pinnedChatUniqueIds.some((pinnedChat) => pinnedChat === uniqueChatId);

			return isPinned;
		},
		[pinnedChatUniqueIds]
	);

	// Event handlers
	const handlePinChat = useCallback(
		(uniqueChatId: string) => {
			const isPinned = isPinnedChat(uniqueChatId);

			if (isPinned) {
				unPinChatMutation.mutate({ chatId: uniqueChatId });
			} else {
				pinChatMutation.mutate({ chatId: uniqueChatId });
			}
		},
		[isPinnedChat, pinChatMutation, unPinChatMutation]
	);

	const handleOpenChat = useCallback(
		({ receiverUserId, uniqueChatId }: { receiverUserId?: string; uniqueChatId?: string }) => {
			setShowListOfChatInInbox(false);

			const selectedChat = filteredInbox?.find((chat) => chat.uniqueChatId === uniqueChatId);

			if (selectedChat) {
				setActiveChat(selectedChat);
			} else if (receiverUserId) {
				startConversation({ receiverUserId: receiverUserId });
			} else if (uniqueChatId) {
				startConversation({ uniqueChatId });
			}
		},
		[filteredInbox, startConversation]
	);

	const handleOpenChatWidget = () => {
		setShowChatWidget(true);
		localStorage.setItem("showChatWidget", String(true));
	};

	const handleCloseChatWidget = () => {
		setShowListOfChatInInbox(false);
		setShowChatWidget(false);
		localStorage.setItem("showChatWidget", String(false));
	};

	const handleOpenInbox = () => {
		setShowListOfChatInInbox(true);

		setActiveChat(undefined);
	};

	const handleCloseInbox = () => {
		setShowListOfChatInInbox(false);
	};

	const handleCloseChat = () => {
		setActiveChat(undefined);
	};

	const handleChatChange = useCallback(
		(uniqueChatId: string) => {
			const chatFromInbox = inbox?.find((chat) => chat.uniqueChatId === uniqueChatId);

			setActiveChat(chatFromInbox);
		},
		[inbox]
	);

	const handleTabChange = (tab: "group" | "direct") => {
		if (tab === "group") {
			if (groupChats && groupChats?.length > 0) {
				handleChatChange(groupChats[0].uniqueChatId);
			}
		} else if (tab === "direct") {
			if (directChats && directChats?.length > 0) {
				handleChatChange(directChats[0].uniqueChatId);
			}
		}

		setActiveTab(tab);
	};

	const setActiveGroupChatByPageOrChannelSlug = useCallback(() => {
		if (pathname === "/chat") {
			return;
		}

		let groupChat;

		const groupChats = inbox?.filter((chat) => chat.type === "group");

		// Show Global Chat inside media / news portal
		if (channelSlug) {
			groupChat = groupChats?.find((chat) => chat.channelSlug === channelSlug);
		} else {
			groupChat = groupChats?.find((chat) =>
				chat.pagesLinked?.some((page) => page.pageId === pathname && page.isActive === true)
			);

			// Show Global chat if no group chat is associated with the page
			if (!groupChat) {
				groupChat = groupChats?.find((chat) =>
					chat.pagesLinked?.some((page) => page.pageId === "/" && page.isActive === true)
				);
			}
		}

		setActiveChat(groupChat);
	}, [channelSlug, inbox, pathname]);

	useEffect(() => {
		const showChatWidgetFromLocalStorage = localStorage.getItem("showChatWidget");
		const defaultShowChatWidget = showChatWidgetFromLocalStorage
			? showChatWidgetFromLocalStorage === "true"
			: isMobileDevice
			? false
			: true;

		setShowChatWidget(defaultShowChatWidget);
	}, [isMobileDevice]);

	useEffect(() => {
		if (inbox) {
			const groupChats = inbox?.filter((chat) => chat.type === "group") || [];
			const directChats = inbox?.filter((chat) => chat.type === "direct") || [];
			const firstGroupChat = groupChats?.[0];

			if (firstGroupChat && !activeChat) {
				setActiveChat(firstGroupChat);
			}

			setFilteredInbox(inbox);
			setGroupChats(groupChats);
			setDirectChats(directChats);
			setPinnedChatUniqueIds(pinnedChats);
		}
	}, [inbox, pinnedChats, activeChat]);

	useEffect(() => {
		setActiveGroupChatByPageOrChannelSlug();
	}, [setActiveGroupChatByPageOrChannelSlug]);

	return (
		<ChatContext.Provider
			value={{
				activeChat,
				inbox: {
					chats: filteredInbox,
					isError: isInboxError,
					isLoading: isInboxLoading,
				},
				activeTab,
				groupChats,
				directChats,
				pinnedChatUniqueIds,
				showChatWidget,
				showListOfChatsInInbox,
				startConversation,
				handleOpenChat,
				handleCloseChat,
				handleOpenInbox,
				handleOpenChatWidget,
				handleCloseChatWidget,
				handleCloseInbox,
				handleChatChange,
				handleTabChange,
				handlePinChat,
				isPinnedChat,
			}}
		>
			{/* Chat Widget */}
			<div className="flex flex-col items-start justify-center md:flex-row md:justify-center">
				<AnimatePresence>
					<motion.div
						className="w-full"
						layout
						initial={{ opacity: 0, scale: 0.8 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.8 }}
						transition={{ duration: isMobileDevice ? 0 : 0.1 }}
					>
						{children}
					</motion.div>

					{!shouldHideChatWidget && showChatWidget ? (
						<motion.div
							exit={{ x: "100%", scale: 0.8 }}
							initial={{ x: "100%", scale: 0.8 }}
							transition={{ type: "spring", stiffness: 200, damping: 20 }}
							animate={{ x: showChatWidget ? 0 : "100%", scale: showChatWidget ? 1 : 0.8 }}
							className={cn("fixed right-0 top-0 z-40 w-full md:w-72 md:p-1 lg:sticky lg:w-80")}
						>
							<ChatWidget />
						</motion.div>
					) : null}
				</AnimatePresence>
			</div>

			{/* Chat Widget Trigger Button */}
			{pathname !== "/chat" && !isInboxLoading ? <ChatBubble /> : null}

			<Suspense fallback={<span>Loading...</span>}>
				<OpenChatTrigger />
			</Suspense>
		</ChatContext.Provider>
	);
};

export const useChat = () => {
	const context = useContext(ChatContext);
	if (context === undefined) {
		throw new Error("useChat must be used within a ChatProvider");
	}

	return context;
};

export default ChatProvider;
